import { reactive } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { supabase, supabaseAdmin } from '../supabase.js'; // Adjust path
import { coreState } from './core.js';
import { Profile } from '../models/Profile.js';
import { Shop } from '../models/Shop.js';
import { ShopMember } from '../models/ShopMember.js';
import { LanguageManager } from '../models/LanguageManager.js';
import { coordinateUtils } from '../utils/coordinateUtils.js';

export const useShopRegistration = () => {

    // --- User Registration (Signup) ---
    async function registerUser(details) {
        let userId = details.id;

        if (!userId && coreState.authSession) {
            userId = coreState.authSession.user.id;
        } else if (!userId) {
            userId = uuidv4();
        }

        const isComplete = details.signupCompleted === true;
        const newUser = new Profile({ ...details, id: userId, signupCompleted: isComplete });

        // 1. Update Local
        coreState.users.set(newUser.id, newUser);
        coreState.activeUser = newUser;

        // 2. Update Supabase DB
        const { error: dbError } = await supabase
            .from('profiles')
            .upsert({
                id: newUser.id,
                email: newUser.email,
                name: newUser.name,
                avatar: newUser.avatar,
                languages: newUser.languages,
                onboarding_intent: newUser.onboarding_intent,
                signup_completed: isComplete,
                qualifications: newUser.qualifications || [],
                specialties: newUser.specialties || []
            });

        if (dbError) {
            console.error("[RegStore] DB Persistence Error (Profile):", dbError);
        } else {
            console.log(`[RegStore] Persisted User Profile: ${newUser.name}`);
        }

        // 3. Update Auth Metadata
        if (coreState.authSession && coreState.authSession.user.id === userId) {
            // Fire and forget - don't block the UI for metadata sync
            supabase.auth.updateUser({
                data: { signup_completed: isComplete }
            }).then(() => console.log("[RegStore] Auth Metadata Synced"))
                .catch(e => console.error("[RegStore] Auth Metadata Sync Failed", e));
        }

        return newUser;
    }

    async function updateUser(userId, updates) {
        console.log(`[RegStore] Updating user ${userId} with`, updates);

        // 1. Optimistic / Local Update
        const user = coreState.users.get(userId);
        if (user) {
            Object.assign(user, updates);
            coreState.users.set(userId, user);
        } else {
            console.warn(`[RegStore] User ${userId} not found in local cache, proceeding with DB update only.`);
        }

        // 2. DB Update
        const dbUpdates = { ...updates };
        if (dbUpdates.signupCompleted !== undefined) {
            dbUpdates.signup_completed = dbUpdates.signupCompleted;
            delete dbUpdates.signupCompleted;
        }

        const { error } = await supabase
            .from('profiles')
            .update(dbUpdates)
            .eq('id', userId);

        if (error) {
            console.error(`[RegStore] Update User Failed:`, error);
        } else {
            console.log(`[RegStore] Successfully updated user ${userId}`);
        }

        return user;
    }

    async function findUserByEmail(email) {
        if (!email) return null;

        // Local Cache
        for (const user of coreState.users.values()) {
            if (user.email && user.email.toLowerCase() === email.toLowerCase()) {
                return user;
            }
        }

        // Admin Search (Backend Simulation)
        if (supabaseAdmin) {
            const { data } = await supabaseAdmin
                .from('profiles')
                .select('*')
                .eq('email', email)
                .single();

            if (data) {
                return new Profile({
                    id: data.id,
                    name: data.name,
                    email: data.email,
                    avatar: data.avatar,
                    signupCompleted: data.signup_completed,
                    stats: data.stats,
                    historySummary: data.history_summary
                });
            }
        }
        return null;
    }

    // --- Business Registration ---
    async function registerShop(ownerId, shopDetails) {
        const shopPayload = {
            name: shopDetails.name,
            type: shopDetails.type || 'dive_center',
            coordinates: (shopDetails.coordinates && shopDetails.coordinates.length === 2)
                ? `(${shopDetails.coordinates[0]},${shopDetails.coordinates[1]})`
                : null,
            location: shopDetails.location,
            website: shopDetails.website,
            style: shopDetails.style || 'default',
            calibration: shopDetails.calibration || {},
            logo_url: shopDetails.logoUrl || null
        };

        const { data: rpcResult, error } = await supabase.rpc('register_shop_and_owner', {
            shop_name: shopPayload.name,
            shop_location: shopPayload.location,
            shop_website: shopPayload.website,
            owner_id: ownerId
        });

        if (error) throw error;

        // Merge original payload with RPC result to preserve all submitted fields
        const newShop = new Shop({
            ...shopPayload,           // Original input (has website, coordinates, etc.)
            ...rpcResult,             // RPC result (has id, created_at, etc.)
            id: rpcResult.id,
            coordinates: shopDetails.coordinates  // Keep original array format
        });

        // [FIX] Explicitly update 'type' because RPC might not set it, 
        // and it is critical for "Partner" flow in MapBuilder.
        if (shopPayload.type) {
            await supabase
                .from('shops')
                .update({ type: shopPayload.type })
                .eq('id', newShop.id);
            newShop.type = shopPayload.type; // Ensure local model is satisfying
        }

        coreState.shops.set(newShop.id, newShop);

        // Add Owner Member (Mock until refresh)
        const member = new ShopMember({
            id: 'temp-' + Date.now(),
            userId: ownerId,
            shopId: newShop.id,
            role: 'Owner',
            status: 'active',
            joinedAt: new Date().toISOString()
        });
        coreState.members.set(member.id, member);

        // Update Context
        coreState.currentShopId = newShop.id;
        coreState.currentUserRole = 'Owner';

        const user = coreState.users.get(ownerId);
        if (user) {
            user.shopId = newShop.id;
            coreState.users.set(ownerId, user);
        }

        return newShop;
    }

    async function updateShop(shopId, updates) {
        console.log(`[RegStore] Updating Shop ${shopId} with`, updates);

        // 1. Optimistic Update (Local) - keep array format for local state
        const shop = coreState.shops.get(shopId);
        if (shop) {
            Object.assign(shop, updates);
            coreState.shops.set(shopId, shop);
        }

        // 2. Prepare DB payload - convert to DB column formats
        const dbPayload = { ...updates };
        if (dbPayload.coordinates) {
            dbPayload.coordinates = coordinateUtils.toPointString(dbPayload.coordinates);
        }
        // Convert 'websites' array to 'website' text column (DB schema is singular)
        if (dbPayload.websites !== undefined) {
            dbPayload.website = Array.isArray(dbPayload.websites) ? dbPayload.websites.join(', ') : (dbPayload.websites || '');
            delete dbPayload.websites;
        }

        // 3. DB Update
        try {
            const { data, error } = await supabase
                .from('shops')
                .update(dbPayload)
                .eq('id', shopId)
                .select()
                .single();

            if (error) throw error;
            console.log("[RegStore] Shop updated in DB:", data);
            return shop;
        } catch (err) {
            console.error("[RegStore] Failed to update shop:", err);
            // Revert? For now, we assume success or user retry.
            throw err;
        }
    }

    async function fetchUserShops() {
        if (!coreState.activeUser) return;

        console.log(`[RegStore] Fetching user shops...`);
        const { data: members, error } = await supabase
            .from('shop_members')
            .select(`*, shop:shops(*)`)
            .eq('user_id', coreState.activeUser.id);

        if (error) {
            console.error("[RegStore] Failed to fetch shops:", error);
            return;
        }

        if (members) {
            members.forEach(m => {
                if (m.shop) {
                    const s = m.shop;
                    const shopModel = new Shop({
                        ...s,
                        id: s.id,
                        name: s.name,
                        type: s.type,
                        location: s.location,
                        coordinates: coordinateUtils.parseCoordinates(s.coordinates),
                        website: s.website || '',
                        style: s.style,
                        calibration: s.calibration,
                        logo_url: s.logo_url || s.logo,
                        status: s.status
                    });
                    coreState.shops.set(s.id, shopModel);
                }
                const memberModel = new ShopMember({
                    id: m.id,
                    userId: m.user_id,
                    shopId: m.shop_id,
                    role: m.role,
                    status: m.status,
                    joinedAt: m.joined_at
                });
                coreState.members.set(m.id, memberModel);
            });
        }
    }

    return {
        registerUser,
        updateUser,
        findUserByEmail,
        registerShop,
        updateShop,
        fetchUserShops
    };
};
